import "./index.scss";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useState, useContext, useRef, useEffect, useCallback} from "react";
import {Link} from "react-router-dom";
import {StoreContext} from "../../pages/StoreContext";

// SliderData 輪播圖資料，isVideo 判斷該張要渲染 <video> 還是 <img>
import SliderData, {isVideo} from "../../assets/data/SliderData";

// slick 套件
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const IMAGE_DURATION = 3000; //靜態圖停留時間，沿用原本的 autoplaySpeed
const STALL_TICK = 500; //watchdog 檢查播放進度的頻率
const STALL_LIMIT = 3000; //播放進度停滯超過此時間就強制換頁

function SampleNextArrow(props) {
    const {className, style, onClick} = props;
    return (
        <div className={className} style={{...style, display: "block"}} onClick={onClick}>
            <FontAwesomeIcon icon="fa-solid fa-chevron-right" />
        </div>
    );
}

function SamplePrevArrow(props) {
    const {className, style, onClick} = props;
    return (
        <div className={className} style={{...style, display: "block"}} onClick={onClick}>
            <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
        </div>
    );
}

const HomeSlider = () => {
    // 處理faq側邊欄active狀態的function
    const {handleToggle} = useContext(StoreContext);

    const {useGtmSender} = useContext(StoreContext);

    // gtm
    const gtmSender = useGtmSender();
    const handleGtmClick = (obj) => {
        gtmSender(obj);
    };

    const [nav1, setNav1] = useState();
    const [nav2, setNav2] = useState();

    const [people, setPeople] = useState(SliderData);

    // 換頁驅動：影片播完(ended)或靜態圖計時到，才切下一張
    const wrapRef = useRef(null);
    const timerRef = useRef(null);
    const [current, setCurrent] = useState(0);

    const goNext = useCallback(() => {
        // innerSlider 為 null 代表 slider 已卸載(例如換頁)，
        // 此時 slickNext() 內部會存取 null 而拋錯，故一併檢查
        if (nav1 && nav1.innerSlider) {
            nav1.slickNext();
        }
    }, [nav1]);

    useEffect(() => {
        const root = wrapRef.current;
        if (!root || !nav1) return;

        clearTimeout(timerRef.current);

        // 用 data-index 定位當前這張的真身(排除 clone)
        // 不用 .slick-current 是因為它要等過場動畫結束才移動，會讓計時晚 1 秒起算
        const video = root.querySelector(`.first-slider-item-s .slick-slide[data-index="${current}"]:not(.slick-cloned) video`);

        // 倒帶只在已載入 metadata 時執行：readyState 為 HAVE_NOTHING 時設 currentTime
        // 會中斷正在進行的載入，導致該支影片永遠停在 HAVE_NOTHING 而播不出來
        const rewind = (v) => {
            if (v.readyState >= v.HAVE_METADATA && v.currentTime !== 0) {
                v.currentTime = 0;
            }
        };

        // 停掉其他影片(含 infinite 產生的 clone)，避免看不見的分身在背景偷跑
        root.querySelectorAll("video").forEach((v) => {
            if (v === video) return;
            v.pause();
            rewind(v);
        });

        // 靜態圖 → 固定秒數換頁
        if (!video) {
            timerRef.current = setTimeout(goNext, IMAGE_DURATION);
            return () => clearTimeout(timerRef.current);
        }

        // 這一張從頭播
        rewind(video);

        let cancelled = false; //cleanup 後阻止非同步回呼再排新的 timer

        // 正常路徑：播完換頁
        // 只對「當前這一個」element 掛 listener，clone 不會有，所以不會跳兩張
        const handleEnded = () => goNext();
        video.addEventListener("ended", handleEnded);

        // watchdog：偵測播放進度是否停滯，停滯超過 STALL_LIMIT 就強制換頁
        // 用進度而非 duration 推算，因為部分 webm 讀不到 duration，
        // 一次涵蓋「自動播放被擋」「duration 讀不到」「緩衝卡死」三種情況
        let lastTime = -1;
        let stalledFor = 0;
        const stallTimer = setInterval(() => {
            if (video.currentTime > lastTime) {
                lastTime = video.currentTime;
                stalledFor = 0;
                return;
            }
            stalledFor += STALL_TICK;
            if (stalledFor >= STALL_LIMIT) {
                clearInterval(stallTimer);
                goNext();
            }
        }, STALL_TICK);

        // 自動播放被明確拒絕(例如瀏覽器政策) → 直接換頁，不必等 watchdog
        video.play().catch(() => {
            if (cancelled) return; //已離開這張，不要再排 timer
            clearInterval(stallTimer);
            timerRef.current = setTimeout(goNext, IMAGE_DURATION);
        });

        return () => {
            cancelled = true;
            clearTimeout(timerRef.current);
            clearInterval(stallTimer);
            video.removeEventListener("ended", handleEnded);
            video.pause();
        };
    }, [current, nav1, goNext]);

    // 設定 Slider 的設定
    const sliderSettingsSmall = {
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        dots: true,
        infinite: true,
        // 用 beforeChange 而非 afterChange：過場一開始就重新計時
        // asNavFor 雙向同步會讓過場多花約 2 秒，等 afterChange 才計時會讓每張多停 2 秒
        beforeChange: (oldIndex, newIndex) => setCurrent(newIndex),
    };
    const sliderSettingsLarge = {
        slidesToShow: 1,
        swipeToSlide: false,
        focusOnSelect: true,
        arrows: false,
        // autoplay 移除：改由影片播完 / 計時器驅動換頁
    };
    return (
        <div className="first-slider-wrap" ref={wrapRef}>
            <div className="first-slider-item-s">
                <Slider asNavFor={nav2} ref={(slider1) => setNav1(slider1)} {...sliderSettingsSmall}>
                    {people.map((person) => {
                        const {
                            slider_id,
                            slider_img,
                            slider_poster,
                            slider_link,
                            slider_template_title,
                            slider_template_element,
                            slider_template_category_name,
                            // for faq
                            category_id,
                        } = person;
                        return (
                            <div key={slider_id} className="first-slider-item-S">
                                <Link
                                    to={slider_link}
                                    onClick={() => {
                                        handleToggle(category_id);
                                        handleGtmClick({
                                            event: "首頁_橫幅廣告",
                                            breadcrumb: "首頁",
                                            model: `${slider_template_element}`,
                                            model_name: `${slider_template_title}`,
                                            model_category: `${slider_template_category_name}`,
                                        });
                                    }}>
                                    {/* 影片不加 autoPlay / loop / onEnded：播放與換頁一律由上方 effect 指令式控制 */}
                                    {isVideo(slider_img) ? (
                                        <video src={slider_img} poster={slider_poster} muted playsInline preload="auto" />
                                    ) : (
                                        <img src={slider_img} alt="" />
                                    )}
                                </Link>
                            </div>
                        );
                    })}
                </Slider>
            </div>

            {/* 背景大圖 */}
            <Slider asNavFor={nav1} ref={(slider2) => setNav2(slider2)} {...sliderSettingsLarge}>
                {people.map((person) => {
                    const {slider_id, slider_img, slider_poster} = person;
                    // 這層有 blur + brightness，放影片看不出差異卻要多付一份解碼成本，故一律用靜態圖。
                    // 影片張若漏填 slider_poster，寧可留空(黑底)也不要把影片路徑塞進 <img> 變成破圖
                    const bgSrc = slider_poster || (isVideo(slider_img) ? null : slider_img);
                    return (
                        <div key={slider_id} className="first-slider-item-l" style={{background: "#000"}}>
                            {bgSrc && <img src={bgSrc} alt="" />}
                        </div>
                    );
                })}
            </Slider>
        </div>
    );
};

export default HomeSlider;
